import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  MessageSquarePlus,
  Bug,
  Gamepad2,
  Sparkles,
  HelpCircle,
  Send,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  ExternalLink,
  UploadCloud,
  ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";

const CATEGORIES = [
  {
    id: "bug",
    label: "Lapor Bug",
    sub: "Link mati / crash",
    icon: Bug,
    color: "from-rose-500/20 to-red-600/20 border-rose-500/40 text-rose-300",
    activeColor: "border-rose-500 bg-rose-950/60 text-rose-200 ring-1 ring-rose-500",
  },
  {
    id: "game_request",
    label: "Request Game",
    sub: "Komisi / Berbayar 💰",
    icon: Gamepad2,
    color: "from-purple-500/20 to-violet-600/20 border-purple-500/40 text-purple-300",
    activeColor: "border-purple-500 bg-purple-950/60 text-purple-200 ring-1 ring-purple-500",
  },
  {
    id: "membership",
    label: "Membership",
    sub: "Saweria / Role DC",
    icon: Sparkles,
    color: "from-amber-500/20 to-yellow-600/20 border-amber-500/40 text-amber-300",
    activeColor: "border-amber-500 bg-amber-950/60 text-amber-200 ring-1 ring-amber-500",
  },
  {
    id: "general",
    label: "Tanya / Saran",
    sub: "Kritik & masukan",
    icon: HelpCircle,
    color: "from-cyan-500/20 to-blue-600/20 border-cyan-500/40 text-cyan-300",
    activeColor: "border-cyan-500 bg-cyan-950/60 text-cyan-200 ring-1 ring-cyan-500",
  },
];

export default function SupportWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Cooldown countdown effect
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Silakan login terlebih dahulu untuk mengirim laporan.");
      return;
    }
    if (!subject.trim() || subject.trim().length < 3) {
      toast.error("Mohon isi judul / topik masalah (minimal 3 karakter).");
      return;
    }
    if (!message.trim() || message.trim().length < 5) {
      toast.error("Mohon jelaskan pesan laporanmu (minimal 5 karakter).");
      return;
    }
    if (category === "bug" && !selectedFile) {
      toast.error("Untuk kategori Lapor Bug, kamu wajib melampirkan foto screenshot bukti!");
      return;
    }
    if (cooldown > 0) {
      toast.error(`Tunggu ${cooldown} detik sebelum mengirim pesan berikutnya.`);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("category", category);
      formData.append("subject", subject.trim());
      formData.append("message", message.trim());
      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      const { data } = await api.post("/support/ticket", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSubmitted(true);
      setCooldown(15);
      toast.success("Laporan berhasil terkirim ke Discord Founder! 🎉");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail || err?.message);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 10MB");
      return;
    }
    setSelectedFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
  };

  const handleRemoveFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleReset = () => {
    setSubmitted(false);
    setSubject("");
    setMessage("");
    handleRemoveFile();
  };

  const displayName = user?.name || (user?.email ? user.email.split("@")[0] : "Cultist");
  const displayRole = (user?.role || "member").replace("_", " ").toUpperCase();

  return (
    <>
      {/* Floating Trigger Button - Ultra sleek on mobile (round icon) & pill on desktop */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex items-center justify-center gap-2 w-11 h-11 sm:w-auto sm:px-4 sm:py-2.5 rounded-full bg-zinc-950/90 hover:bg-zinc-900 border border-rose-500/40 hover:border-rose-400 text-rose-300 hover:text-rose-100 shadow-xl shadow-rose-950/40 backdrop-blur-md transition-all duration-300 hover:scale-105 active:scale-95 group focus:outline-none focus:ring-2 focus:ring-rose-500/50"
        title="Layanan Bantuan & Laporan"
        aria-label="Bantuan dan Laporan"
      >
        <div className="relative flex items-center justify-center">
          <MessageSquarePlus className="w-5 h-5 text-rose-400 group-hover:text-rose-300 transition-colors" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping opacity-75" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500" />
        </div>
        <span className="hidden sm:inline text-xs sm:text-sm font-semibold tracking-wide text-zinc-200 group-hover:text-white">
          Bantuan & Request
        </span>
      </button>

      {/* Main Support Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[94vw] max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl bg-zinc-950/95 border border-rose-500/30 p-4 sm:p-6 text-white shadow-2xl backdrop-blur-2xl focus:outline-none">
          <DialogHeader className="space-y-1.5 pb-2 border-b border-zinc-800/80">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-600 to-rose-900 grid place-items-center shadow-md shadow-rose-950">
                <span className="font-mystic text-sm text-white">響</span>
              </div>
              <DialogTitle className="text-base sm:text-lg font-bold text-zinc-100">
                Pusat Bantuan & Laporan
              </DialogTitle>
            </div>
            <DialogDescription className="text-xs sm:text-sm text-zinc-400">
              Lapor link mati, request game VN/RPG, atau tanya info membership langsung ke Discord Founder.
            </DialogDescription>
          </DialogHeader>

          {/* User Status Bar */}
          {user ? (
            <div className="mt-3 flex items-center justify-between p-2.5 rounded-xl bg-zinc-900/70 border border-zinc-800 text-xs">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar user={user} size={36} />
                <div className="truncate">
                  <span className="font-semibold text-zinc-100 truncate block text-xs sm:text-sm">{displayName}</span>
                  <span className="text-[10px] text-emerald-400 flex items-center gap-1 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span> Terverifikasi
                  </span>
                </div>
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <RoleBadge role={user.role} badges={user.badges} size="xs" />
              </div>
            </div>
          ) : (
            <div className="mt-3 p-3 rounded-xl bg-amber-950/20 border border-amber-500/30 text-xs space-y-2">
              <div className="flex items-start gap-2 text-amber-300">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>Kamu belum masuk ke akun Kyouka TL. Silakan login terlebih dahulu agar Founder dapat merespon laporanmu.</p>
              </div>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs transition-colors"
              >
                Masuk / Login Sekarang <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          )}

          {submitted ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/10 border border-rose-500/40 grid place-items-center text-rose-400">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h4 className="text-base sm:text-lg font-bold text-zinc-100">
                  Laporan Terkirim ke Discord Founder! 🎉
                </h4>
                <p className="text-xs sm:text-sm text-zinc-400 max-w-sm mx-auto">
                  Pesanmu sudah otomatis terkirim ke Discord Founder Kyouka TL. Founder akan segera mengeceknya.
                </p>
              </div>
              <div className="pt-2 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-xs sm:text-sm font-medium text-zinc-200 transition-colors"
                >
                  Kirim Pesan Lain
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-xs sm:text-sm font-semibold text-white transition-colors"
                >
                  Tutup
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-3 space-y-3.5">
              {/* Category Selection Grid - 2x2 for perfect mobile touch layout */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-zinc-400 tracking-wider uppercase">
                  Pilih Kategori Laporan
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    const isSelected = category === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategory(cat.id)}
                        className={`flex items-start gap-2 p-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? cat.activeColor
                            : "bg-zinc-900/50 hover:bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        <Icon className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold leading-tight truncate">{cat.label}</p>
                          <p className="text-[10px] text-zinc-500 truncate">{cat.sub}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Info notice for Game Request (Commission/Paid) */}
              {category === "game_request" && (
                <div className="p-3 rounded-xl bg-purple-950/40 border border-purple-500/40 space-y-2 text-xs animate-in fade-in duration-200">
                  <div className="flex items-start gap-2 text-purple-200">
                    <Sparkles className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-purple-300">Request Game Bersifat Komisi / Berbayar</p>
                      <p className="text-[11px] text-zinc-300 mt-0.5 leading-relaxed">
                        Penerjemahan game VN/RPG khusus dilakukan via sistem komisi berbayar. Kamu bisa isi form ini atau langsung chat/DM Founder di Discord untuk diskusi harga & estimasi pengerjaan.
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://discord.com/users/1293704480054968414"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-semibold text-xs transition-colors shadow-md shadow-purple-950"
                  >
                    <span>💬 Chat Langsung / DM Founder di Discord</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}

              {/* Subject Input */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-400 tracking-wider uppercase">
                  Judul / Topik Singkat <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  disabled={!user || submitting}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={
                    category === "bug"
                      ? "Contoh: Link Google Drive part 2 game XXX mati"
                      : category === "game_request"
                      ? "Contoh: Request TL Visual Novel 'XXX'"
                      : category === "membership"
                      ? "Contoh: Konfirmasi pembayaran Saweria belum aktif"
                      : "Contoh: Pertanyaan seputar download / saran web"
                  }
                  className="w-full text-xs sm:text-sm bg-zinc-900/80 border border-zinc-800 rounded-xl px-3 py-2 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 disabled:opacity-50"
                  maxLength={150}
                />
              </div>

              {/* Message Input */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-zinc-400 tracking-wider uppercase">
                    Detail Pesan <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-500">{message.length}/2000</span>
                </div>
                <textarea
                  required
                  rows={3}
                  disabled={!user || submitting}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Jelaskan kendala, link postingan, atau detail request-mu sedetail mungkin..."
                  className="w-full text-xs sm:text-sm bg-zinc-900/80 border border-zinc-800 rounded-xl p-3 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 resize-none disabled:opacity-50"
                  maxLength={2000}
                />
              </div>

              {/* Screenshot File Upload (Native) */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-400 tracking-wider uppercase">
                  Upload Screenshot / Bukti{" "}
                  {category === "bug" ? (
                    <span className="text-rose-400 text-[10px] font-semibold">* (Wajib untuk Lapor Bug)</span>
                  ) : (
                    <span className="text-zinc-500 text-[10px]">(Opsional)</span>
                  )}
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                />

                {!selectedFile ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!user || submitting}
                    className={`w-full p-2.5 rounded-xl border border-dashed transition-all flex items-center justify-center gap-2 text-xs group focus:outline-none disabled:opacity-50 ${
                      category === "bug"
                        ? "border-rose-500/60 bg-rose-950/20 hover:bg-rose-950/40 text-rose-300 hover:text-rose-200 shadow-sm shadow-rose-950/50"
                        : "border-zinc-700 hover:border-rose-500/50 bg-zinc-900/40 hover:bg-zinc-900 text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    <UploadCloud className="w-4 h-4 text-rose-400 group-hover:scale-110 transition-transform" />
                    <span>{category === "bug" ? "Pilih Foto Bukti Screenshot (Wajib)" : "Pilih Foto dari HP / Komputer"}</span>
                  </button>
                ) : (
                  <div className="flex items-center justify-between p-2 rounded-xl bg-zinc-900/80 border border-zinc-700 text-xs animate-in fade-in duration-200">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-10 h-10 object-cover rounded-lg border border-zinc-700 shrink-0"
                      />
                      <div className="truncate">
                        <p className="text-zinc-200 font-medium truncate">{selectedFile.name}</p>
                        <p className="text-[10px] text-zinc-400">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="p-1.5 rounded-lg hover:bg-rose-950/60 text-zinc-400 hover:text-rose-400 transition-colors"
                      title="Hapus foto"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Submit Action */}
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={!user || submitting || cooldown > 0}
                  className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-rose-600 via-rose-700 to-rose-800 hover:from-rose-500 hover:to-rose-700 active:scale-[0.99] text-white font-semibold text-xs sm:text-sm shadow-lg shadow-rose-950/50 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Mengirim ke Founder...</span>
                    </>
                  ) : cooldown > 0 ? (
                    <span>Tunggu {cooldown}s sebelum kirim lagi</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Kirim ke Discord Founder 🚀</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
